import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import Echo from 'laravel-echo';
import Pusher from 'pusher-js'; // eslint-disable-line no-unused-vars
import { Dropdown, Modal } from 'react-bootstrap'
import Select from 'react-select'

import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'

import { findKeyFromArrayByValue, formatDate, formatMoney, returnSign } from 'helpers/helpers';
import { GET, httpOk, POST } from 'helpers/api';
import { getSocketConfig } from 'helpers/env';

// Вкладки страницы: ручные синхронизации справочника (кнопкой), поток вызовов кассы
// и сводка проблем. Разделены, потому что отвечают на разные вопросы: «я нажал — оно
// доехало?», «касса на связи?» и «что именно разъехалось?».
const TAB_MANUAL = 'manual'
const TAB_LOGS = 'logs'
const TAB_ISSUES = 'issues'

// Эндпоинты, которые пишет SyncLogger. Список короткий и меняется редко, поэтому
// держим его здесь, а не тянем отдельным запросом.
const ENDPOINTS = [
	'sync-cheque',
	'sync-cheques',
	'sync-temporary-cheque',
	'sync/stop-list',
	'sync-data',
	'v1/products/balance',
]

const ISSUE_TYPES = [
	'product_mismatch',
	'price_mismatch',
	'product_missing',
	'no_write_off',
	'request_failed',
]

function Index() {
	const { t } = useTranslation()
	const date = new Date()

	const socketRef = useRef(null)

	const reduxAccount = useSelector(state => state.account)

	const [activeTab, setActiveTab] = useState(TAB_MANUAL)
	const [data, setData] = useState([])
	const [poses, setPoses] = useState([])
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterDataExist, setFilterDataExist] = useState(false)
	const [selectedLog, setSelectedLog] = useState(null)
	const [showLogModal, setShowLogModal] = useState(false)
	const [filterData, setFilterData] = useState({
		'start_date': formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
		'end_date': formatDate(new Date(), 'yyyy-MM-dd'),
		'pos_id': reduxAccount.pos_id,
	})
	const [pagination, setPagination] = useState({
		url: '/synchronizations',
		response: null
	})

	function baseUrl(tab = activeTab) {
		if (tab === TAB_LOGS) return '/synchronizations/logs'
		if (tab === TAB_ISSUES) return '/synchronizations/issues'

		return '/synchronizations'
	}

	async function searchWithFilter(nextFilterData = null, tab = activeTab) {
		var filterUrl = baseUrl(tab)
		var urlParams = ""
		checkFilter()
		const currentFilterData = nextFilterData || filterData

		// На вкладке проблем точка необязательна: владельцу сети нужно видеть все свои
		// кассы разом, иначе разъехавшийся филиал придётся искать перебором.
		if (currentFilterData.pos_id && (tab !== TAB_ISSUES || currentFilterData.pos_id))
			urlParams += returnSign(urlParams) + 'pos_id=' + currentFilterData.pos_id
		if (currentFilterData.search)
			urlParams += returnSign(urlParams) + 'search=' + currentFilterData.search
		if (currentFilterData.page)
			urlParams += returnSign(urlParams) + 'page=' + currentFilterData.page

		if (tab !== TAB_MANUAL) {
			if (currentFilterData.start_date)
				urlParams += returnSign(urlParams) + 'start_date=' + currentFilterData.start_date
			if (currentFilterData.end_date)
				urlParams += returnSign(urlParams) + 'end_date=' + currentFilterData.end_date
		}

		if (tab === TAB_LOGS) {
			if (currentFilterData.only_problems)
				urlParams += returnSign(urlParams) + 'only_problems=1'
			if (currentFilterData.endpoint)
				urlParams += returnSign(urlParams) + 'endpoint=' + currentFilterData.endpoint
		}

		if (tab === TAB_ISSUES && currentFilterData.issue_type)
			urlParams += returnSign(urlParams) + 'issue_type=' + currentFilterData.issue_type

		if (`${baseUrl(tab)}${urlParams}` !== filterUrl)
			setFilterDataExist(true)

		filterUrl = filterUrl + urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ ...pagination, 'url': filterUrl, 'response': response.data })
		}

		setDropdownIsOpen(false)
	}

	function changeTab(tab) {
		if (tab === activeTab) return

		setActiveTab(tab)
		setData([])

		const nextFilterData = { ...filterData, page: null }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData, tab)
	}

	function handleSearchChange(search) {
		const nextFilterData = { ...filterData, search, page: null }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	function toggleOnlyProblems() {
		const nextFilterData = { ...filterData, only_problems: !filterData.only_problems, page: null }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	async function sync() {
		var response = await POST('/synchronizations', { 'pos_id': filterData.pos_id }, { loader: true })

		if (httpOk(response)) searchWithFilter()
	}

	async function showLog(id) {
		const response = await GET(`/synchronizations/logs/${id}`, {}, { loader: true })

		if (httpOk(response)) {
			setSelectedLog(response.data)
			setShowLogModal(true)
		}
	}

	function checkFilter() {
		if (
			filterData.pos_id === null &&
			filterData.search === ''
		) {
			setFilterDataExist(false)
		}
	}

	function resetFilter() {
		const nextFilterData = {
			pos_id: reduxAccount.pos_id,
			search: '',
			start_date: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
			end_date: formatDate(new Date(), 'yyyy-MM-dd'),
		}
		setFilterData(nextFilterData)
		setFilterDataExist(false)
		searchWithFilter(nextFilterData)
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	async function paginate(page) {
		const nextFilterData = { ...filterData, 'page': page }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	function connectSocket() {
		// Тот же выбор «локальный / боевой», что и у API: иначе при
		// yarn start:prod запросы шли бы в боевой бэк, а сокет — в локальный.
		const { wsHost, authEndpoint, isProd } = getSocketConfig()

		if (socketRef.current) {
			socketRef.current.leave(`user.${filterData.pos_id}`);
		}

		try {
			socketRef.current = new Echo({
				broadcaster: 'pusher',
				key: "mison_key",
				wsHost: wsHost,
				wsPort: 80,
				wssPort: 443,
				enabledTransports: ['ws', 'wss'],
				disableStats: true,
				encrypted: isProd ? true : false,
				useTLS: isProd ? true : false,
				forceTLS: isProd ? true : false,
				authEndpoint: authEndpoint,
				auth: {
					headers: {
						'Authorization': `Bearer ${reduxAccount.token}`
					}
				},
			});
		} catch (error) {
			console.log(error)
		}

		socketRef.current.private(`user.${filterData.pos_id}`)
			.listen('Connection', (payload) => {
				console.log('payload', payload)

				// Результат ручной синхронизации обновляет только свою вкладку: на журнале
				// это дёргало бы выборку без причины.
				if (payload.type === 'synchronizationResult' && activeTab === TAB_MANUAL) {
					searchWithFilter()
				}
			});
	}

	function returnClassStatus(status) {
		if (status === -1) {
			return "bg-danger"
		}
		if (status === 1) {
			return "bg-success"
		}
	}

	// Тип списания и тип позиции клиент видит числами, а понимает словами.
	function writeOffTypeName(value) {
		if (String(value) === '1') return t('sync_write_off_type_1')
		if (String(value) === '2') return t('sync_write_off_type_2')

		return value
	}

	function issueValue(item, value) {
		if (item.field === 'write_off_type') return writeOffTypeName(value)
		if (item.field === 'sale_price' && value !== null) return formatMoney(value)

		return value
	}

	// Одна строка сводки — одно предложение, которое можно прочитать без документации.
	function issueDescription(item) {
		if (item.issue_type === 'product_missing') {
			return t('sync_issue_product_missing_text')
		}

		if (item.issue_type === 'no_write_off') {
			return t('sync_issue_no_write_off_text')
		}

		if (item.issue_type === 'request_failed') {
			return item.cloud_value || t('sync_issue_request_failed')
		}

		return `${t('sync_field_' + item.field)}: ${t('sync_on_cash')} «${issueValue(item, item.cash_value)}» → ${t('sync_in_cloud')} «${issueValue(item, item.cloud_value)}»`
	}

	function issueRowClass(item) {
		// Красным — то, что уже стоило склада или денег. Цена и тип позиции важны,
		// но сами по себе остаток не рушат, поэтому им хватает жёлтого.
		if (item.issue_type === 'no_write_off' || item.issue_type === 'product_missing') return 'text-danger'
		if (item.issue_type === 'product_mismatch' && item.field === 'write_off_type') return 'text-danger'

		return 'text-warning'
	}

	useEffect(() => {
		getPoses()
		connectSocket()

		return () => {
			if (socketRef.current) {
				socketRef.current.disconnect()
			}
		}
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	async function getPoses() {
		const response = await GET('/helper/poses')
		if (httpOk(response)) setPoses(response.data)
	}

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('synchronizations')} [{formatMoney(data?.total)}]
				</h4>
				<div className="d-flex">
					<button className="btn-rounded btn btn-primary"
						onClick={() => sync()}>
						{t('synchronize')}
					</button>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<ul className="nav nav-tabs mb-3">
						<li className="nav-item">
							<button type="button"
								className={'nav-link ' + (activeTab === TAB_MANUAL ? 'active' : '')}
								onClick={() => changeTab(TAB_MANUAL)}>
								{t('sync_tab_manual')}
							</button>
						</li>
						<li className="nav-item">
							<button type="button"
								className={'nav-link ' + (activeTab === TAB_LOGS ? 'active' : '')}
								onClick={() => changeTab(TAB_LOGS)}>
								{t('sync_tab_logs')}
							</button>
						</li>
						<li className="nav-item">
							<button type="button"
								className={'nav-link ' + (activeTab === TAB_ISSUES ? 'active' : '')}
								onClick={() => changeTab(TAB_ISSUES)}>
								{t('sync_tab_issues')}
							</button>
						</li>
					</ul>

					<div className="d-flex flex-wrap mb-3 gap-2">
						<div>
							<FilterSearch
								value={filterData.search}
								onSearchChange={handleSearchChange}>
							</FilterSearch>
						</div>
						<div>
							<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen}>
								<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
									{t('filter')}
								</Dropdown.Toggle>

								<Dropdown.Menu className="dropdown-filter">
									<div className="row">
										<div className="form-group col-md-6">
											<label>{t('pos')}</label>
											<Select
												options={poses}
												value={poses.find(option => option.id === filterData.pos_id) || ''}
												onChange={(option) => setFilterData({ ...filterData, 'pos_id': option.id })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>
										{activeTab !== TAB_MANUAL &&
											<>
												<div className="form-group col-md-3">
													<label>{t('from')}</label>
													<input type="date" className="form-control"
														value={filterData.start_date || ''}
														onChange={(e) => setFilterData({ ...filterData, 'start_date': e.target.value })} />
												</div>
												<div className="form-group col-md-3">
													<label>{t('to')}</label>
													<input type="date" className="form-control"
														value={filterData.end_date || ''}
														onChange={(e) => setFilterData({ ...filterData, 'end_date': e.target.value })} />
												</div>
											</>
										}
									</div>
									{activeTab === TAB_LOGS &&
										<div className="row mt-2">
											<div className="form-group col-md-6">
												<label>{t('sync_endpoint')}</label>
												<select className="form-select"
													value={filterData.endpoint || ''}
													onChange={(e) => setFilterData({ ...filterData, 'endpoint': e.target.value })}>
													<option value="">{t('all')}</option>
													{ENDPOINTS.map((item, index) => (
														<option value={item} key={index}>{item}</option>
													))}
												</select>
											</div>
										</div>
									}
									{activeTab === TAB_ISSUES &&
										<div className="row mt-2">
											<div className="form-group col-md-6">
												<label>{t('sync_issue_type')}</label>
												<select className="form-select"
													value={filterData.issue_type || ''}
													onChange={(e) => setFilterData({ ...filterData, 'issue_type': e.target.value })}>
													<option value="">{t('all')}</option>
													{ISSUE_TYPES.map((item, index) => (
														<option value={item} key={index}>{t('sync_issue_' + item)}</option>
													))}
												</select>
											</div>
										</div>
									}
									<div className="row mt-3">
										<div className="col-md-12">
											<div className="d-flex justify-content-end gap-2">
												<button className="btn btn-secondary"
													onClick={() => setDropdownIsOpen(false)}>
													{t('cancel')}
												</button>
												<button className="btn btn-primary"
													onClick={() => searchWithFilter()}>
													{t('apply')}
												</button>
											</div>
										</div>
									</div>
								</Dropdown.Menu>
							</Dropdown>
						</div>
						{activeTab === TAB_LOGS &&
							<div className="vertical-center">
								<div className="form-check">
									<input className="form-check-input" type="checkbox"
										id="onlyProblems"
										checked={!!filterData.only_problems}
										onChange={() => toggleOnlyProblems()} />
									<label className="form-check-label" htmlFor="onlyProblems">
										{t('sync_only_problems')}
									</label>
								</div>
							</div>
						}
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{filterData.pos_id &&
									<span className="me-2">
										<b>{t('pos')}: </b>
										{findKeyFromArrayByValue(poses, 'id', filterData.pos_id, 'name')}
									</span>
								}
								{filterData.search &&
									<span className="me-2">
										<b>{t('search')}: </b>
										<span>{filterData.search}</span>
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase"
									onClick={resetFilter}>
									{t('reset')}
								</button>
							</div>
						</div>
					}

					{activeTab === TAB_MANUAL &&
						<div className="table-responsive">
							<table className="table table-bordered mb-0">
								<thead>
									<tr>
										<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
										<th style={{ 'width': '15%' }}>{t('pos')}</th>
										<th style={{ 'width': '50%' }}>{t('note')}</th>
										<th style={{ 'width': '10%' }} className="text-center">ID</th>
										<th style={{ 'width': '15%' }} className="text-center">{t('date')}</th>
										<th style={{ 'width': '10%' }} className="text-center">{t('status')}</th>
									</tr>
								</thead>
								<tbody>
									{data?.data?.map((item, index) => (
										<tr key={index}>
											<td className="text-center">{item.row_number}</td>
											<td>{item.pos_name}</td>
											<td className="td-ellipsis" title={item.message}>
												{item.message}
											</td>
											<td className="text-center">{item.id}</td>
											<td className="text-center">{formatDate(item.created_at, 'dd.MM.yy HH:mm')}</td>
											<td className={returnClassStatus(item.status)}></td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					}

					{activeTab === TAB_LOGS &&
						<div className="table-responsive">
							<table className="table table-bordered mb-0">
								<thead>
									<tr>
										<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
										<th style={{ 'width': '18%' }}>{t('sync_endpoint')}</th>
										<th style={{ 'width': '10%' }} className="text-center">{t('cheque')}</th>
										<th style={{ 'width': '8%' }} className="text-center">{t('sync_items')}</th>
										<th style={{ 'width': '8%' }} className="text-center">{t('sync_duration')}</th>
										<th style={{ 'width': '26%' }}>{t('note')}</th>
										<th style={{ 'width': '10%' }} className="text-center">{t('version')}</th>
										<th style={{ 'width': '12%' }} className="text-center">{t('date')}</th>
										<th style={{ 'width': '8%' }} className="text-center">{t('status')}</th>
									</tr>
								</thead>
								<tbody>
									{data?.data?.map((item, index) => (
										<tr key={index} className="cursor-pointer"
											onClick={() => showLog(item.id)}>
											<td className="text-center">{item.row_number}</td>
											<td>{item.endpoint}</td>
											<td className="text-center">{item.cheque_id}</td>
											<td className="text-center">{item.items_count}</td>
											<td className="text-center">{item.duration_ms}</td>
											<td className="td-ellipsis" title={item.error_message}>
												{item.error_message}
												{!item.error_message && item.issues_count > 0 &&
													<span className="text-warning">
														{t('sync_issues_found')}: {item.issues_count}
													</span>
												}
											</td>
											<td className="text-center">{item.client_version}</td>
											<td className="text-center">{formatDate(item.created_at, 'dd.MM.yy HH:mm:ss')}</td>
											<td className="text-center">
												{item.is_error
													? <span className="bg-danger text-white px-2">{item.status_code}</span>
													: (item.issues_count > 0
														? <span className="bg-warning text-white px-2">{item.status_code}</span>
														: <span className="text-success">{item.status_code}</span>)
												}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					}

					{activeTab === TAB_ISSUES &&
						<div className="table-responsive">
							<table className="table table-bordered mb-0">
								<thead>
									<tr>
										<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
										<th style={{ 'width': '12%' }}>{t('pos')}</th>
										<th style={{ 'width': '14%' }}>{t('sync_issue_type')}</th>
										<th style={{ 'width': '18%' }}>{t('product')}</th>
										<th style={{ 'width': '30%' }}>{t('note')}</th>
										<th style={{ 'width': '7%' }} className="text-center">{t('sync_hits')}</th>
										<th style={{ 'width': '7%' }} className="text-center">{t('cheque')}</th>
										<th style={{ 'width': '12%' }} className="text-center">{t('sync_last_seen')}</th>
									</tr>
								</thead>
								<tbody>
									{data?.data?.map((item, index) => (
										<tr key={index}>
											<td className="text-center">{item.row_number}</td>
											<td>{item.pos_name}</td>
											<td className={issueRowClass(item)}>
												{t('sync_issue_' + item.issue_type)}
											</td>
											<td>{item.product_name}</td>
											<td className="td-ellipsis" title={issueDescription(item)}>
												{issueDescription(item)}
											</td>
											<td className="text-center">{item.hits}</td>
											<td className="text-center">
												{item.sync_log_id
													? <span className="text-primary cursor-pointer"
														onClick={() => showLog(item.sync_log_id)}>
														{item.cheque_id}
													</span>
													: item.cheque_id
												}
											</td>
											<td className="text-center">{formatDate(item.last_seen_at, 'dd.MM.yy HH:mm')}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					}

					{pagination.response &&
						<FilterPagination
							pagination={pagination}
							paginate={paginate}>
						</FilterPagination>
					}
				</div>
			</div>

			{/* MODAL */}
			<Modal show={showLogModal} animation={false} centered size="lg"
				dialogClassName="update-modal-width"
 onHide={() => setShowLogModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{selectedLog?.endpoint} · {formatDate(selectedLog?.created_at, 'dd.MM.yy HH:mm:ss')}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="d-flex flex-wrap gap-3 mb-3">
						<div><b>{t('pos')}:</b> {selectedLog?.pos_name}</div>
						<div><b>{t('status')}:</b> {selectedLog?.status_code}</div>
						<div><b>{t('sync_duration')}:</b> {selectedLog?.duration_ms} ms</div>
						<div><b>{t('version')}:</b> {selectedLog?.client_version || '—'}</div>
						<div><b>{t('cheque')}:</b> {selectedLog?.cheque_id || '—'}</div>
					</div>

					{selectedLog?.error_message &&
						<div className="alert alert-danger">{selectedLog.error_message}</div>
					}

					<label className="mb-1"><b>{t('sync_payload')}</b></label>
					{selectedLog?.payload
						? <pre className="p-2 border rounded"
							style={{ 'maxHeight': '420px', 'overflow': 'auto', 'fontSize': '.78rem' }}>
							{JSON.stringify(
								typeof selectedLog.payload === 'string'
									? JSON.parse(selectedLog.payload)
									: selectedLog.payload,
								null, 2)}
						</pre>
						: <div className="text-muted">{t('sync_no_payload')}</div>
					}
				</Modal.Body>
				<Modal.Footer>
					<button className="btn btn-warning"
						onClick={() => setShowLogModal(false)}>
						{t('cancel')}
					</button>
				</Modal.Footer>
			</Modal>
			{/* MODAL */}
		</>
	)
}

export default Index
